import CachedIcon from '@mui/icons-material/Cached';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { GridToolbarColumnsButton, GridToolbarContainer, GridToolbarDensitySelector, GridToolbarFilterButton } from '@mui/x-data-grid';
import { subMonths } from 'date-fns';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import NoRowOverlay from '../../../assets/images/icons/NoRowOverlay';
import {
   ControlpanelSecondaryButton,
   ControlpanelSuccessButton,
   ControlpanelWarningButton,
   GrayIconButton,
} from '../../../components/StyledButton';
import { CustomDialog, StyledDataGrid } from '../../../components/StyledComponents';
import CustomGridToolbarQuickFilter from '../CustomGridToolbarQuickFilter';
import CustomPagination from '../CustomPagination';
import CustomDatePicker from './../../../components/CustomDatePicker';
import CustomColumnsPanel from '../../../components/CustomColumnsPanel';

const RestoreEventsDialog = ({ handleOpenRestoreEvent, onClose }) => {
   const { t } = useTranslation();
   const handleCloseDialog = () => {
      onClose();
   };

   const [formData, setFormData] = useState({
      selectedFrom: subMonths(new Date(), 1),
      selectedTo: new Date(),
   });

   const handleInputChange = (name, value) => {
      setFormData((prevData) => ({
         ...prevData,
         [name]: value,
      }));
   };

   const columnData = [
      {
         field: 'employeeName',
         headerName: 'Name',
         minWidth: 150,
         flex: 1,
         hideable: true,
         valueGetter: (params) => (params.row.employeeName ? params.row.employeeName : '-'),
      },
      {
         field: 'employeeId',
         headerName: 'ID',
         minWidth: 150,
         flex: 1,
         hideable: true,
         valueGetter: (params) => (params.row.employeeId ? params.row.employeeId : '-'),
      },
      {
         field: 'shiftName',
         headerName: 'Shift name',
         minWidth: 150,
         flex: 1,
         hideable: true,
         valueGetter: (params) => (params.row.shiftName ? params.row.shiftName : '-'),
      },
      {
         field: 'shiftStart',
         headerName: 'Shift start',
         minWidth: 200,
         flex: 1,
         hideable: true,
         valueGetter: (params) => (params.row.shiftStart ? params.row.shiftStart : '-'),
      },
      {
         field: 'eventType',
         headerName: 'Direction and status',
         minWidth: 250,
         flex: 1,
         hideable: true,
         valueGetter: (params) => (params.row.eventType ? params.row.eventType : '-'),
      },
      {
         field: 'sensorData',
         headerName: 'Sensor data',
         minWidth: 150,
         flex: 1,
         hideable: true,
         valueGetter: (params) => (params.row.sensorData ? params.row.sensorData : 'No sensor data'),
      },
      {
         field: 'timeGMT',
         headerName: 'Time (GMT)',
         minWidth: 200,
         flex: 1,
         hideable: true,
         valueGetter: (params) => (params.row.timeGMT ? params.row.timeGMT : '-'),
      },
      {
         field: 'time',
         headerName: 'Time',
         minWidth: 150,
         flex: 1,
         hideable: true,
         valueGetter: (params) => (params.row.time ? params.row.time : '-'),
      },
      {
         field: 'timeZone',
         headerName: 'Time zone',
         minWidth: 150,
         flex: 1,
         hideable: true,
         valueGetter: (params) => (params.row.timeZone ? params.row.timeZone : '-'),
      },
      {
         field: 'location',
         headerName: 'Location',
         minWidth: 150,
         flex: 1,
         hideable: true,
         valueGetter: (params) => (params.row.location ? params.row.location : 'Not available'),
      },
      {
         field: 'address',
         headerName: 'Address',
         minWidth: 150,
         flex: 1,
         hideable: true,
         valueGetter: (params) => (params.row.address ? params.row.address : 'Not available'),
      },
      {
         field: 'deviceCode',
         headerName: 'Device code',
         minWidth: 150,
         flex: 1,
         hideable: true,
         valueGetter: (params) => (params.row.deviceCode ? params.row.deviceCode : '-'),
      },
      {
         field: 'deviceDescription',
         headerName: 'Device description',
         minWidth: 150,
         flex: 1,
         hideable: true,
         valueGetter: (params) => (params.row.deviceDescription ? params.row.deviceDescription : '-'),
      },
      {
         field: 'deviceOS',
         headerName: 'Device OS',
         minWidth: 150,
         flex: 1,
         hideable: true,
         valueGetter: (params) => (params.row.deviceOS ? params.row.deviceOS : '-'),
      },
      {
         field: 'clientAppName',
         headerName: 'Client app name',
         minWidth: 150,
         flex: 1,
         hideable: true,
         valueGetter: (params) => (params.row.clientAppName ? params.row.clientAppName : '-'),
      },
      {
         field: 'createdTime',
         headerName: 'Created time',
         minWidth: 200,
         flex: 1,
         hideable: true,
         valueGetter: (params) => (params.row.createdTime ? params.row.createdTime : '-'),
      },
      {
         field: 'modifiedTime',
         headerName: 'Modified time',
         minWidth: 200,
         flex: 1,
         hideable: true,
         valueGetter: (params) => (params.row.modifiedTime ? params.row.modifiedTime : '-'),
      },
      {
         field: 'modifiedBy',
         headerName: 'Modified by',
         minWidth: 150,
         flex: 1,
         hideable: true,
         valueGetter: (params) => (params.row.modifiedBy ? params.row.modifiedBy : '-'),
      },
      {
         field: 'description',
         headerName: 'Description',
         minWidth: 150,
         flex: 1,
         hideable: true,
         valueGetter: (params) => (params.row.description ? params.row.description : '-'),
      },
      {
         field: 'restrictionGroupName',
         headerName: 'Restriction group name',
         minWidth: 250,
         flex: 1,
         hideable: true,
         valueGetter: (params) => (params.row.restrictionGroupName ? params.row.restrictionGroupName : '-'),
      },
      {
         field: 'editedBy',
         headerName: 'Edited by',
         minWidth: 150,
         flex: 1,
         hideable: true,
         valueGetter: (params) => (params.row.editedBy ? params.row.editedBy : '-'),
      },
   ];

   const [columnVisibilityModel, setColumnVisibilityModel] = useState({
      eventName: true,
      eventId: true,
      shiftName: true,
      shiftStart: false,
      directionAndStatus: true,
      sensorData: false,
      timeGMT: false,
      time: true,
      timeZone: false,
      location: true,
      address: true,
      deviceCode: false,
      deviceDescription: false,
      deviceOS: false,
      clientAppName: false,
      createdTime: false,
      modifiedTime: false,
      modifiedBy: false,
      description: false,
      restrictionGroupName: false,
      editedBy: false,
   });

   const [rowData, setRowData] = useState([]);
   const [gridLoading, setGridLoading] = useState(false);

   const CustomToolbar = () => {
      return (
         <div>
            <GridToolbarContainer className='flex justify-between items-center'>
               <div className='flex gap-3'>
                  <GridToolbarColumnsButton />
                  <GridToolbarFilterButton />
                  <GridToolbarDensitySelector />
               </div>
               <div className='mt-2'>
                  <CustomGridToolbarQuickFilter />
               </div>
            </GridToolbarContainer>
         </div>
      );
   };

   return (
      <CustomDialog open transitionDuration={300} maxWidth={'xl'} onClose={handleCloseDialog} keepMounted>
         <DialogTitle>
            <div className='flex justify-between'>
               <div className='flex items-center gap-4'>
                  <p className='text-xl font-semibold '>Restore events</p>
               </div>
               <GrayIconButton onClick={handleCloseDialog}>
                  <CloseIcon />
               </GrayIconButton>
            </div>
         </DialogTitle>
         <DialogContent>
            <div className='border border-[#EFEFEF] py-3 px-5 rounded'>
               <div className='grid grid-col-12 md:grid-cols-10 gap-x-5 gap-y-5'>
                  <div className='col-span-12 md:col-span-4'>
                     <label className='block'>{t('common.from')}</label>
                     <div className='mt-2'>
                        <CustomDatePicker
                           name='selectedFrom'
                           value={formData.selectedFrom}
                           onChange={(newDate) => handleInputChange('selectedFrom', newDate)}
                           readOnly={true}
                        />
                     </div>
                  </div>
                  <div className='col-span-12 md:col-span-4'>
                     <label className='block'>{t('common.to')}</label>
                     <div className='mt-2'>
                        <CustomDatePicker
                           name='selectedTo'
                           value={formData.selectedTo}
                           onChange={(newDate) => handleInputChange('selectedTo', newDate)}
                           readOnly={true}
                        />
                     </div>
                  </div>
                  <div className='col-span-12 md:col-span-2 md:flex justify-end items-end'>
                     <ControlpanelWarningButton>
                        <CachedIcon />
                        {t('button.refresh')}
                     </ControlpanelWarningButton>
                  </div>
               </div>
            </div>
            <div className='min-h-[400px]'>
               <StyledDataGrid
                  sx={{
                     '.MuiDataGrid-main': {
                        height: '350px',
                     },
                  }}
                  rows={rowData}
                  columns={columnData}
                  loading={gridLoading}
                  // paginationMode='server'
                  // pageSize={pageSize}
                  // page={page}
                  // rowCount={rowCount}
                  // totalPages={totalPages}
                  // onPageChange={(newPage) => setPage(newPage)}
                  // onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
                  slots={{
                     toolbar: CustomToolbar,
                     pagination: CustomPagination,
                     // loadingOverlay: LinearProgress,
                     noRowsOverlay: NoRowOverlay,
                     columnsPanel: CustomColumnsPanel,
                  }}
                  slotProps={{
                     toolbar: {
                        showQuickFilter: true,
                     },
                     noRowsOverlay: {
                        title: 'No events',
                     },
                     // pagination: {
                     //     page,
                     //     pageSize,
                     //     rowCount: rowCount,
                     //     totalPages: totalPages,
                     //     onPageChange: setPage,
                     //     onPageSizeChange: setPageSize,
                     // },
                  }}
                  columnVisibilityModel={columnVisibilityModel}
                  onColumnVisibilityModelChange={(newModel) => setColumnVisibilityModel(newModel)}
                  // onRowSelectionModelChange={onRowsSelectionHandler}
                  initialState={{
                     filter: {
                        filterModel: {
                           items: [],
                           quickFilterValues: [''],
                        },
                     },
                  }}
               />
            </div>
         </DialogContent>
         <DialogActions>
            <div className='flex gap-2 px-4 py-4'>
               <ControlpanelSecondaryButton onClick={handleCloseDialog}>
                  <CloseIcon />
                  <p>{t('button.cancel')}</p>
               </ControlpanelSecondaryButton>
               <ControlpanelSuccessButton
               //  onClick={handleOpenExportSelectedEventlog}
               >
                  <ContentCopyIcon />
                  <p>{t('button.restore')}</p>
               </ControlpanelSuccessButton>
            </div>
         </DialogActions>
      </CustomDialog>
   );
};

export default RestoreEventsDialog;
